import { cva } from "class-variance-authority";
/**
 * Container styles for the text field wrapper
 */
export const textFieldContainerVariants = cva("relative flex flex-col", {
    variants: {
        fullWidth: {
            true: "w-full",
            false: "w-64",
        },
    },
    defaultVariants: {
        fullWidth: false,
    },
});
/**
 * Input wrapper styles (contains label, input, icons, prefix/suffix)
 * Per FMC Figma: 56px height, 1px border default, 3px border on focus
 */
export const textFieldWrapperVariants = cva([
    "relative",
    "flex",
    "items-center",
    "transition-all",
    "duration-200",
    "ease-out",
    "min-h-[56px]",
], {
    variants: {
        variant: {
            filled: [
                "bg-surface-container-highest",
                "rounded-t-md-xs",
                "border-b-2",
                "border-on-surface-variant",
                "focus-within:border-b-[3px]",
            ],
            outlined: [
                "bg-transparent",
                "rounded-[4px]",
                "border",
                "border-outline",
            ],
        },
        hasError: {
            true: "",
            false: "",
        },
        disabled: {
            true: "opacity-[0.38] cursor-not-allowed pointer-events-none",
            false: "",
        },
        isTextarea: {
            true: "min-h-[80px] items-start",
            false: "",
        },
    },
    compoundVariants: [
        {
            variant: "filled",
            hasError: true,
            className: "!border-b-[4px] !border-error focus-within:!border-b-[4px] focus-within:!border-error",
        },
        {
            variant: "outlined",
            hasError: true,
            className: "!border-[4px] !border-error focus-within:!border-[4px] focus-within:!border-error",
        },
    ],
    defaultVariants: {
        variant: "outlined",
        hasError: false,
        disabled: false,
        isTextarea: false,
    },
});
/**
 * Floating label styles following FMC Design System Figma specs
 *
 * Default state: Label centered vertically inside at 16px (body-large)
 * Floating state: Label at top-[-12px] at 12px (body-small) with bg cutout
 */
export const textFieldLabelVariants = cva([
    "absolute",
    "left-4", // 16px to match input padding
    "transition-all",
    "duration-200",
    "ease-out",
    "pointer-events-none",
    "origin-top-left",
    "font-sans", // Match Figma font (Poppins)
    "text-on-surface-variant", // Figma Schemes-On-Surface-Variant #44474C
], {
    variants: {
        variant: {
            filled: "",
            outlined: "",
        },
        hasError: {
            true: "text-error",
            false: "",
        },
        focused: {
            true: "",
            false: "",
        },
        floating: {
            // Floating: Move to top, shrink to 12px (body-small)
            true: [
                "text-[12px]",
                "leading-[16px]",
                "tracking-[0.4px]",
                "font-sans", // Match Figma font (Poppins)
                "top-0",
                "-translate-y-1/2",
            ],
            // Default: Centered inside at 16px (body-large)
            false: [
                "text-[16px]",
                "leading-[24px]",
                "tracking-[0.5px]",
                "font-sans", // Match Figma font (Poppins)
                "top-1/2",
                "-translate-y-1/2",
            ],
        },
        disabled: {
            true: "opacity-[0.38]",
            false: "",
        },
    },
    compoundVariants: [
        {
            hasError: false,
            className: "text-on-surface-variant",
        },
        {
            floating: true,
            focused: true,
            hasError: false,
            className: "text-on-surface-variant",
        },
        {
            floating: true,
            focused: false,
            hasError: false,
            className: "text-on-surface-variant",
        },
        {
            floating: false,
            hasError: false,
            className: "text-on-surface-variant",
        },
        // Outlined + floating = responsive background color
        // Mobile: match page background (surface-container-low), Tablet/Desktop: match card (white)
        {
            variant: "outlined",
            floating: true,
            className: "px-1 left-4 bg-surface-container-low md:bg-white", // Mobile: page bg, Tablet/Desktop: card bg
        },
        // Filled + floating = move to top-left corner
        {
            variant: "filled",
            floating: true,
            className: "top-2 translate-y-0 left-4", // Updated to match 16px padding
        },
    ],
    defaultVariants: {
        variant: "outlined",
        hasError: false,
        focused: false,
        floating: false,
        disabled: false,
    },
});
/**
 * Input element styles
 * Content area is 48px per Figma spec
 */
export const textFieldInputVariants = cva([
    "flex-1",
    "w-full",
    "bg-transparent",
    "text-on-surface",
    "text-[16px]",
    "leading-[24px]",
    "tracking-[0.5px]",
    "placeholder:text-on-surface-variant",
    "focus:outline-none",
    "disabled:cursor-not-allowed",
    // Equal vertical padding for centered content
    "py-3",
], {
    variants: {
        variant: {
            filled: "pl-4 pr-1", // 16px left padding, 4px right padding to match Figma
            outlined: "pl-4 pr-1", // 16px left padding, 4px right padding to match Figma
        },
        hasStartIcon: {
            true: "pl-0",
            false: "",
        },
        hasEndIcon: {
            true: "pr-0",
            false: "",
        },
        isTextarea: {
            true: "resize-y min-h-[60px] py-4",
            false: "",
        },
    },
    defaultVariants: {
        variant: "outlined",
        hasStartIcon: false,
        hasEndIcon: false,
        isTextarea: false,
    },
});
/**
 * Helper/Error text styles
 * Matches Figma: 12px, 16px line height, 400 weight, 0.4px letter spacing, Poppins font
 */
export const textFieldHelperVariants = cva("text-[12px] leading-[16px] font-normal tracking-[0.4px] font-sans flex-1", {
    variants: {
        hasError: {
            true: "text-error",
            false: "text-on-surface-variant",
        },
    },
    defaultVariants: {
        hasError: false,
    },
});
/**
 * Character counter styles
 */
export const textFieldCounterVariants = cva("text-label-sm", {
    variants: {
        hasError: {
            true: "text-error",
            false: "text-on-surface-variant",
        },
    },
    defaultVariants: {
        hasError: false,
    },
});
/**
 * Prefix/Suffix text styles
 */
export const textFieldPrefixSuffixVariants = cva("text-body-lg text-on-surface-variant", {
    variants: {
        hasError: {
            true: "text-error",
            false: "",
        },
    },
    defaultVariants: {
        hasError: false,
    },
});
/**
 * Icon wrapper styles
 */
export const textFieldIconVariants = cva("w-5 h-5 text-on-surface-variant flex-shrink-0", {
    variants: {
        position: {
            start: "ml-3",
            end: "mr-3",
        },
        hasError: {
            true: "text-error",
            false: "",
        },
    },
    defaultVariants: {
        hasError: false,
    },
});
//# sourceMappingURL=text-field.styles.js.map